import React from 'react';
import styled from '@emotion/styled';
import KvTitleImg from 'ui/images/top_for_agent/kv_title@2x.png';
import { Only } from 'ui/components/elements/Only/Only';
import { mqMin } from 'utils/responsive';
import { useNavigate } from 'react-router-dom';
import { AgentSignUpRequest } from 'ui/components/company/screens/modals/AgentSignUpRequest/AgentSignUpRequest';
import { useModal } from 'ui/components/screens/modals/useModal';

const Container = styled.div`
    position: relative;
    display: flex;
    justify-content: center;
    align-content: flex-start;
    flex-wrap: wrap;
    width: 100%;
    max-width: 430px;
    max-height: 605px;
    aspect-ratio: 86 / 121; /*430 * 605*/
    margin: 0 auto 0 auto;

    ${mqMin('NARROW')} {
        height: 700px;
        max-height: initial;
        max-width: initial;
        aspect-ratio: initial;
    }
`;

const H2 = styled.h2`
    position: absolute;
    top: 11.40495867768595%;
    left: 50%;
    translate: -50% 25px;
    width: 83.72093023255814%;
    opacity: 0;
    animation: kvFadeinY 0.8s cubic-bezier(0.33, 1, 0.68, 1) 1.3s forwards;

    ${mqMin('NARROW')} {
        top: 192px;
        width: 432px;
        animation: kvFadeinY 0.8s cubic-bezier(0.33, 1, 0.68, 1) 1.8s forwards;
    }
`;

const Text = styled.div`
    position: absolute;
    top: 27.60330578512397%;
    left: 50%;
    translate: -50% 25px;
    width: 100%;
    text-align: center;
    font-size: min(3.8vw, 16px);
    line-height: 1.7em;
    letter-spacing: 0.1em;
    opacity: 0;
    animation: kvFadeinY 0.8s cubic-bezier(0.33, 1, 0.68, 1) 1.3s forwards;

    ${mqMin('NARROW')} {
        top: 296px;
        font-size: 1rem;
        animation: kvFadeinY 0.8s cubic-bezier(0.33, 1, 0.68, 1) 1.8s forwards;
    }
`;

const KvCopy = styled.div`
    position: absolute;
    top: 41.48760330578512%;
    left: 50%;
    translate: -50% 25px;
    width: 100%;
    text-align: center;
    font-size: min(5.11vw, 22px);
    line-height: 1.7em;
    letter-spacing: 0.1em;
    opacity: 0;
    animation: kvFadeinY 0.8s cubic-bezier(0.33, 1, 0.68, 1) 1.3s forwards;

    ${mqMin('NARROW')} {
        top: 384px;
        font-size: 1.25rem; /*20px*/
        opacity: 0;
        animation: kvFadeinY 0.8s cubic-bezier(0.33, 1, 0.68, 1) 1.8s forwards;
    }

    strong {
        position: relative;

        &::before {
            position: absolute;
            bottom: -8px;
            left: 50%;
            transform-origin: center left;
            translate: -50% 0;
            scale: 0 1;
            content: '';
            display: block;
            width: 100%;
            height: 2px;
            background-color: var(--color-navy);
            animation: kvLine 1s cubic-bezier(0.33, 1, 0.68, 1) 2s forwards;
        }

        &::after {
            position: absolute;
            bottom: -4px;
            left: 50%;
            transform-origin: center left;
            translate: -50% 0;
            scale: 0 1;
            content: '';
            display: block;
            width: 100%;
            height: 2px;
            background-color: var(--color-navy);
            animation: kvLine 1s cubic-bezier(0.33, 1, 0.68, 1) 2s forwards;
        }
    }
`;

const KvLinkPc = styled.div`
    display: none;

    ${mqMin('NARROW')} {
        position: absolute;
        top: 485px;
        left: 50%;
        translate: -50% 0;
        width: 100%;
        display: block;
        opacity: 0;
        animation: kvFadeinY 0.8s cubic-bezier(0.33, 1, 0.68, 1) 2.4s forwards;

        p {
            text-align: center;
            font-size: 1.125rem; /*18px*/
            line-height: 1.7em;
            letter-spacing: 0.1em;
            margin-bottom: 9px;

            strong {
                position: relative;
                font-size: 1.5rem;

                &::after {
                    position: absolute;
                    top: -20px;
                    left: 50%;
                    content: '・';
                    transform: translateX(-50%);
                    font-size: 20px;
                }
            }
        }

        a {
            display: block;
            background-color: var(--color-orange);
            border-radius: 8px;
            border: solid 2px #fff;
            color: #fff;
            text-align: center;
            text-decoration: none;
            padding: 16px 20px;
            margin: 0 auto 0 auto;
            transition: all 0.3s;

            position: relative;
            width: 320px;
            overflow: hidden;
            margin: 0 auto 0 auto;
            padding: 20px 0;

            &::before {
                position: absolute;
                content: '';
                display: inline-block;
                top: -180px;
                left: 0;
                width: 30px;
                height: 100%;
                background-color: rgba(255, 236, 227, 0.8);
                transition: 300ms;
                animation: kvBtn 1s ease-in-out 2.6s;
            }
        }
    }
`;

export const KvMainText: React.FC = () => {
    const navigate = useNavigate();
    const preOpenModal = useModal();
    const { openModal, createModal } = preOpenModal;
    const goToRegister = () => {
        if (import.meta.env.PROD) {
            openModal();
        } else {
            navigate('/send-registration-mail/');
        }
    };
    return (
        <Container>
            {createModal(<AgentSignUpRequest />)}
            <H2>
                <img src={KvTitleImg} alt="求む、M&Aアドバイザー。" />
            </H2>
            <Text>
                多くの企業が、
                <Only sp>
                    <br />
                </Only>
                M&Aのベストパートナーを探しています。
            </Text>
            <KvCopy>
                意義ある顧客開拓に、
                <Only sp>
                    <br />
                </Only>
                <strong>M&Aプラットフォーム「MAny」</strong>。
            </KvCopy>
            <KvLinkPc>
                <p>
                    マッチング成立までは<strong>基</strong>
                    <strong>本</strong>
                    <strong>無</strong>
                    <strong>料</strong>
                </p>
                <a href="#" onClick={() => goToRegister()}>
                    無料登録して企業を探す
                </a>
            </KvLinkPc>
        </Container>
    );
};
